import { cloneElement, isValidElement } from 'react';
import { useInsforge } from '../provider/InsforgeProvider';

export interface SignUpButtonProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * SignUpButton Component
 *
 * A simple unstyled button that redirects to the hosted sign-up page.
 * If children is a valid React element, it will be rendered with the redirect logic attached.
 *
 * @example
 * ```tsx
 * <SignUpButton />
 * ```
 *
 * @example
 * ```tsx
 * <SignUpButton>
 *   <button className="fancy-button">Register Now</button>
 * </SignUpButton>
 * ```
 */
export function SignUpButton({ children, className }: SignUpButtonProps) {
  const { afterSignInUrl, baseUrl } = useInsforge();

  const handleClick = () => {
    const redirectUrl = new URL(afterSignInUrl, window.location.origin).href;
    const authUrl = new URL('/auth/sign-up', baseUrl);
    authUrl.searchParams.set('redirect', redirectUrl);
    window.location.replace(authUrl.toString());
  };

  if (children && isValidElement(children)) {
    const originalOnClick = (children.props as { onClick?: (e: React.MouseEvent) => void })
      ?.onClick;

    return cloneElement(children, {
      onClick: (e: React.MouseEvent) => {
        handleClick();
        if (originalOnClick) {
          originalOnClick(e);
        }
      },
    } as Record<string, unknown>);
  }

  // Default button
  return (
    <button type="button" className={className} onClick={handleClick}>
      {children || 'Sign up'}
    </button>
  );
}
